export interface RejectionReason {
  id: string;
  title: string;        // rótulo curto (para o select)
  description: string;  // texto explicativo (exibido abaixo do select)
}

export interface RejectionReasons {
  motivos: RejectionReason[];
  detalhesSugeridos?: string[];
}

const DTF_TEXTIL: RejectionReasons = {
  motivos: [
    { id: 'dtf-tracos-finos', title: 'Traços/contraluz muito finos', description: 'Linhas e vãos negativos < 0,5 pt (~0,2 mm) tendem a falhar após a transferência.' },
    { id: 'dtf-baixa-resolucao', title: 'Baixa resolução no tamanho final', description: 'Imagens raster < 200–300 dpi ficam pixeladas; ampliação posterior não corrige.' },
    { id: 'dtf-transparencias', title: 'Transparências/sobretom mal aplicados', description: 'Efeitos suaves geram halos e perda no branco de fundo do DTF.' },
    { id: 'dtf-cor-inconsistente', title: 'Gestão de cor inconsistente', description: 'Arte sem perfil/baixo contraste entre cores vizinhas compromete a leitura.' },
    { id: 'dtf-sobreposicao', title: 'Sobreposição/desorganização de camadas', description: 'Objetos empilhados causam bordas fantasmas e áreas duplicadas.' },
    { id: 'dtf-fontes', title: 'Fontes pequenas ou não convertidas', description: 'Risco de substituição e ilegibilidade; preferir conversão em curvas e evitar fontes muito finas.' },
    { id: 'dtf-fundo', title: 'Fundo indevido/“caixa” visível', description: 'Fundo sólido/pixelado indevido; exigir fundo transparente quando for área vazada.' },
  ],
  detalhesSugeridos: [
    'Texto ilegível',
    'Contraste insuficiente',
    'Alinhamento incorreto',
    'Fundo pixelado',
    'Branding fora do padrão',
    'Arquivo incompleto'
  ]
};

const SUBLIMACAO: RejectionReasons = {
  motivos: [
    { id: 'sub-fonte', title: 'Fonte não convertida', description: 'Pode ocorrer substituição automática; enviar texto em curvas.' },
    { id: 'sub-cor', title: 'Cores fora do padrão sublimático', description: 'Sem perfil/prova → grandes variações; fluos/metalizados não reproduzem fielmente.' },
    { id: 'sub-dimensao', title: 'Dimensão incorreta e sem sangria', description: 'Para all-over/molde exigir sangria (5–10 mm) e escala correta.' },
    { id: 'sub-resolucao', title: 'Resolução insuficiente', description: 'Raster < 200–300 dpi no tamanho final resulta em pixelização.' },
    { id: 'sub-contraste', title: 'Contraste insuficiente em áreas críticas', description: 'Sobretom/transparências fazem detalhes desaparecerem; reforçar contraste.' },
    { id: 'sub-tile', title: 'Padrões mal tileados', description: 'Emendas visíveis/repetição desalinhada; enviar tile fechado e testado.' },
    { id: 'sub-fundo', title: 'Fundo não recortado/indesejado', description: '“Caixa” impressa em aplicações localizadas; exigir fundo transparente.' },
  ],
  detalhesSugeridos: [
    'Tile desalinhado',
    'Sem prova de cor',
    'Sem sangria',
    'Raster em baixa'
  ]
};

const ADESIVO_UV_CORTE: RejectionReasons = {
  motivos: [
    { id: 'uv-sem-cut', title: 'Sem linha de corte definida', description: 'Ausência de spot CutContour (ou equivalente) impede o recorte no RIP.' },
    { id: 'uv-cut-fill', title: 'Linha de corte com stroke/fill incorretos', description: 'Deve ser hairline, sem preenchimento e cor spot única.' },
    { id: 'uv-cut-aberta', title: 'Linha de corte aberta/descontínua', description: 'Caminhos não fechados impedem o recorte preciso.' },
    { id: 'uv-sem-sangria', title: 'Sem sangria para recorte', description: 'Exigir 2–3 mm além da linha de corte para evitar borda branca.' },
    { id: 'uv-geo-inviavel', title: 'Geometrias inviáveis', description: 'Cantos muito agudos/raios mínimos abaixo do tolerado podem rasgar.' },
    { id: 'uv-duplicados', title: 'Objetos duplicados/sobrepostos', description: 'Duas CutContours ou sobreposição de vetores geram recortes duplicados.' },
    { id: 'uv-raster-baixo', title: 'Raster baixo/efeitos não flatten', description: 'Transparências não aplainadas e imagens < 300 dpi geram halo/serrilhado.' },
  ],
  detalhesSugeridos: [
    'CutContour ausente',
    'CutContour com fill',
    'Caminho aberto',
    'Sem sangria',
    'Raio mínimo inválido',
    'Objetos duplicados'
  ]
};

const DTF_UV_ROLO: RejectionReasons = {
  motivos: [
    { id: 'dtfuv-tracos-finos', title: 'Traços ultra finos/serifas delicadas', description: 'Linhas < 0,5 pt podem sumir ou quebrar após impressão/transferência.' },
    { id: 'dtfuv-transparencias', title: 'Transparências/sombras raster em baixa', description: 'Efeitos com alpha/multiply geram serrilhado/manchas; preferir vetor/flatten.' },
    { id: 'dtfuv-raster-baixo', title: 'Raster em baixa no tamanho final', description: 'Imagens < 300 dpi ficam pixeladas, principalmente em relevo UV.' },
    { id: 'dtfuv-microdetalhes', title: 'Microdetalhes em excesso (ilhas)', description: 'Muitos elementos minúsculos isolados dificultam a transferência/aderência.' },
    { id: 'dtfuv-ocultos-duplicados', title: 'Objetos ocultos ou duplicados', description: 'Camadas escondidas/cópias sobrepostas geram impressão duplicada/manchas.' },
    { id: 'dtfuv-dimensoes', title: 'Dimensões/margens inadequadas', description: 'Arte sem margem de segurança ou fora da escala da aplicação.' },
    { id: 'dtfuv-branco-verniz', title: 'Uso incorreto de branco/verniz', description: 'Spot colors mal nomeados ou aplicados em excesso/sem propósito.' },
  ],
  detalhesSugeridos: [
    'Serifas delicadas',
    'Alpha/multiply em baixa',
    'Islhas em excesso',
    'Branco/verniz incorreto'
  ]
};

export function getRejectionReasonsByProduto(produtoNome?: string): RejectionReasons {
  const p = (produtoNome || '').toUpperCase();
  if (p.includes('DTF') && (p.includes('TÊXTIL') || p.includes('TEXTIL'))) return DTF_TEXTIL;
  if (p.includes('SUBLIM')) return SUBLIMACAO;
  if (p.includes('ADESIVO') && (p.includes('UV') || p.includes('RECORTE'))) return ADESIVO_UV_CORTE;
  if (p.includes('DTF') && p.includes('UV')) return DTF_UV_ROLO;
  // fallback: DTF TEXTIL como padrão mais comum
  return DTF_TEXTIL;
}

export function getRejectionReasonsByProId(proId?: number): RejectionReasons {
  if (!proId && proId !== 0) return DTF_TEXTIL;
  // Mapas simples e editáveis diretamente no código do site
  const DTF_TEXTIL_IDS = new Set([4, 5, 15, 30]);
  const DTF_UV_IDS = new Set([3, 2, 14, 33]);

  if (DTF_TEXTIL_IDS.has(Number(proId))) return DTF_TEXTIL;
  if (DTF_UV_IDS.has(Number(proId))) return DTF_UV_ROLO;
  // Heurísticas adicionais podem ser acrescentadas aqui
  return DTF_TEXTIL;
}

export const REJECTION_PRESETS = {
  DTF_TEXTIL,
  SUBLIMACAO,
  ADESIVO_UV_CORTE,
  DTF_UV_ROLO,
};


